import React from 'react';
import { Subtitle } from 'components/Text';
import { Country, CountryState, Location } from 'types';
import get from 'lodash/get';
import Services from 'services';
import { isRegionSupported } from 'helpers/geo';
import CountryPicker from 'components/Picker/Country';
import CountryStatePicker from 'components/Picker/CountryState';
import { t } from 'components/Translate';
import InputLabel from 'components/Input/Label';

import styles from './CountryAndStatePicker.module.scss';

type Props = {
  country: Country | null,
  state: CountryState | null,
  onCountryChange: (country: Country) => void,
  onStateChange: (state: CountryState) => void,
};

type State = {
  countries: Country[],
  states: CountryState[],
  location: Location | null,
};

class CountryAndStatePicker extends React.Component<Required<Props>, State> {
  static defaultProps: OptionalProps<Props> = {
    country: null,
    state: null,
  };

  state: State = {
    countries: [],
    states: [],
    location: null,
  };

  componentDidMount() {
    this.fetch();
  }
  private async fetch() {
    const [location, countries] = await Promise.all([
      Services.get('geo').getLocation(),
      this.fetchCountries(),
    ]);

    if (this.props.country) return;

    const country = countries.find(({ code }) => code === location.countryCode);
    if (!country) return;

    this.props.onCountryChange(country);
    if (country.finalRegion) return;

    const states = await this.fetchStates();
    const state = (states || []).find(({ code }) => code === location.regionCode);
    if (!state) return;

    this.props.onStateChange(state);
  }
  private async fetchCountries() {
    const countries = await Services.get('geo').getCountries();
    this.setState({ countries });
    return countries;
  }
  private fetchStates = async () => {
    if (!this.props.country || this.props.country.finalRegion) return;

    const { subRegions } = await Services.get('geo').getCountry(this.props.country);
    this.setState({ states: subRegions });
    return subRegions;
  };

  private get hasStates() {
    return !!(this.props.country && !this.props.country.finalRegion);
  }
  private get isRegionSupported() {
    const { state, country } = this.props;
    return isRegionSupported(country, state);
  }
  private get states() {
    if (!this.hasStates || !this.state.states) return [];

    return this.state.states;
  }
  private setCountry = (country: Country) => {
    this.setState({ states: [] }, this.fetchStates);
    this.props.onCountryChange(country);
  };
  private setCountryState = (state: CountryState) => {
    this.props.onStateChange(state);
  };

  render() {
    return (
      <>
        <InputLabel
          className={ 'mb-0 t-left d-block' }
          text={ t('onboarding.signup.label.country') }
        >
          <CountryPicker
            className={ styles.picker }
            countries={ this.state.countries }
            selected={ get(this.props.country, 'code', null) }
            onChange={ this.setCountry }
          />
          { this.hasStates &&
            <CountryStatePicker
              className={ styles.picker }
              states={ this.states }
              selected={ get(this.props.state, 'id', null) }
              onChange={ this.setCountryState }
            />
          }
          { !this.isRegionSupported &&
            <Subtitle
              type="small"
              color="dark-light"
            >
              { t('onboarding.signup.regionUnsupported') }
            </Subtitle>
          }
        </InputLabel>
      </>
    );
  }
}

export default CountryAndStatePicker;
